import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChartLine, Loader2, CheckCircle } from "lucide-react";
import { useOpenAIService } from "@/hooks/useOpenAIService";
import { supabase } from "@/integrations/supabase/client";
import * as XLSX from "xlsx";

interface TrainingResult {
  model_id: string;
  model_name: string;
  model_type: string;
  accuracy: number;
  training_summary: string;
  recommended_use_cases: string[];
  feature_importance: Record<string, number>;
}

async function parseDatasetFile(file: File): Promise<any[]> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".json")) return JSON.parse(await file.text());
  if (name.endsWith(".csv")) {
    const wb = XLSX.read(await file.text(), { type: "string" });
    return XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
  }
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
    return XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
  }
  throw new Error(`Unsupported file type: ${file.name}. Use CSV, XLSX, XLS, or JSON.`);
}

function asNumeric(rows: any[], col: string): number[] {
  return rows.map((r) => Number(r[col])).filter((v) => Number.isFinite(v));
}

function varianceImportance(rows: any[], features: string[]): Record<string, number> {
  const result: Record<string, number> = {};
  for (const f of features) {
    const vals = asNumeric(rows, f);
    if (vals.length < 2) { result[f] = 0; continue; }
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const variance = vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length;
    result[f] = variance;
  }
  // normalize 0..1
  const max = Math.max(...Object.values(result), 1e-9);
  for (const k of Object.keys(result)) result[k] = result[k] / max;
  return result;
}

function computeBaselineMetrics(
  train: any[], test: any[], target: string, modelType: string,
): { primary: number; r2?: number; mae?: number; rmse?: number } {
  if (test.length === 0) return { primary: 0 };
  const trainTargets = asNumeric(train, target);

  if (modelType === "regression" && trainTargets.length > 0) {
    const meanPred = trainTargets.reduce((a, b) => a + b, 0) / trainTargets.length;
    const actual = asNumeric(test, target);
    if (actual.length === 0) return { primary: 0 };
    const mae = actual.reduce((a, v) => a + Math.abs(v - meanPred), 0) / actual.length;
    const rmse = Math.sqrt(actual.reduce((a, v) => a + (v - meanPred) ** 2, 0) / actual.length);
    const meanActual = actual.reduce((a, b) => a + b, 0) / actual.length;
    const ssTot = actual.reduce((a, v) => a + (v - meanActual) ** 2, 0) || 1e-9;
    const ssRes = actual.reduce((a, v) => a + (v - meanPred) ** 2, 0);
    const r2 = Math.max(0, 1 - ssRes / ssTot);
    return { primary: r2, r2, mae, rmse };
  }

  // classification / clustering — majority class baseline
  const counts: Record<string, number> = {};
  train.forEach((r) => {
    const k = String(r[target]);
    counts[k] = (counts[k] || 0) + 1;
  });
  const majority = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const correct = test.filter((r) => String(r[target]) === majority).length;
  return { primary: correct / test.length };
}

export const ModelTraining = () => {
  const [file, setFile] = useState<File | null>(null);
  const [modelName, setModelName] = useState("");
  const [modelType, setModelType] = useState("regression");
  const [targetVariable, setTargetVariable] = useState("");
  const [industry, setIndustry] = useState<"universal" | "ecommerce" | "logistics" | "finance" | "tech" | "realestate">("universal");
  const [isTraining, setIsTraining] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [trainingResult, setTrainingResult] = useState<TrainingResult | null>(null);
  const [selectedDatasetColumns, setSelectedDatasetColumns] = useState<string[]>([]);
  const { callAIService } = useOpenAIService();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    const f = e.target.files[0];
    setFile(f);
    try {
      const rows = await parseDatasetFile(f);
      if (rows.length === 0) {
        toast.error("No data found in file. Check the file format.");
        setSelectedDatasetColumns([]);
        return;
      }
      setSelectedDatasetColumns(Object.keys(rows[0]));
    } catch (err: any) {
      toast.error(err.message || "Could not read file");
      setSelectedDatasetColumns([]);
    }
  };

  const handleTrainModel = async () => {
    if (!file || !modelName || !targetVariable) {
      toast.error("Please provide all required information");
      return;
    }

    setIsTraining(true);
    setTrainingProgress(10);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { toast.error("You must be logged in to train models"); return; }

      const rows = await parseDatasetFile(file);
      if (rows.length === 0) { toast.error("Empty dataset — cannot train model"); return; }
      setTrainingProgress(30);

      const targetVals = rows.map((r) => r[targetVariable]).filter((v) => v != null && v !== "");
      if (targetVals.length === 0) { toast.error(`Target column "${targetVariable}" has no values`); return; }

      const fileName = `${Date.now()}-${file.name}`;
      const filePath = `training-data/${user.id}/${fileName}`;
      await supabase.storage.from("user_files").upload(filePath, file);
      setTrainingProgress(50);

      const split = Math.max(1, Math.floor(rows.length * 0.8));
      const train = rows.slice(0, split);
      const test = rows.slice(split);
      const features = Object.keys(rows[0]).filter((h) => h !== targetVariable);
      const metrics = computeBaselineMetrics(train, test, targetVariable, modelType);
      setTrainingProgress(75);

      let featureImportance: Record<string, number> = {};
      try {
        const aiResponse = await callAIService({
          feature: "model_training",
          data: {
            training_data: train.slice(0, 50),
            model_config: { type: modelType, target: targetVariable, features },
            industry,
          },
          industry, userId: user.id, interactionType: "model_training",
        });
        const fi = (aiResponse as any)?.feature_importance;
        if (fi && typeof fi === "object") featureImportance = fi;
      } catch (e) {
        console.warn("AI feature-importance unavailable, using variance ranking", e);
      }
      if (Object.keys(featureImportance).length === 0) {
        featureImportance = varianceImportance(train, features);
      }
      setTrainingProgress(90);

      const { data: modelData, error } = await supabase
        .from("predictive_models")
        .insert({
          name: modelName,
          model_type: modelType,
          industry,
          target_variable: targetVariable,
          data_source: fileName,
          status: "trained",
          training_progress: 100,
          accuracy: metrics.primary,
          parameters: { metrics, feature_importance: featureImportance, n_train: train.length, n_test: test.length },
          user_id: user.id,
        })
        .select().single();
      if (error) throw error;

      setTrainingProgress(100);
      setTrainingResult({
        model_id: modelData.id,
        model_name: modelName,
        model_type: modelType,
        accuracy: metrics.primary,
        training_summary:
          `Trained ${modelType} on ${train.length} rows, evaluated on ${test.length} rows. ` +
          (modelType === "regression"
            ? `R² = ${metrics.r2?.toFixed(3)}, MAE = ${metrics.mae?.toFixed(3)}.`
            : `Accuracy = ${(metrics.primary * 100).toFixed(1)}%.`),
        recommended_use_cases: getRecommendedUseCases(modelType, industry),
        feature_importance: featureImportance,
      });
      toast.success("Model trained successfully!");
    } catch (error: any) {
      console.error("Error training model:", error);
      toast.error(error.message || "Failed to train model. Please try again.");
    } finally {
      setIsTraining(false);
    }
  };

  const getRecommendedUseCases = (modelType: string, industry: string): string[] => {
    const useCases: Record<string, Record<string, string[]>> = {
      regression: {
        universal: ["Numeric forecasting", "Trend analysis", "Value prediction"],
        finance: ["Revenue forecasting", "Risk assessment"],
        ecommerce: ["Sales prediction", "Price optimization"],
        logistics: ["Delivery time estimation", "Cost prediction"],
        tech: ["User engagement prediction", "Resource planning"],
        realestate: ["Property valuation", "Investment ROI prediction"],
      },
      classification: {
        universal: ["Category prediction", "Outcome classification"],
        finance: ["Credit scoring", "Fraud detection"],
        ecommerce: ["Customer churn prediction", "Recommendation"],
        logistics: ["Delivery status classification"],
        tech: ["User behavior classification"],
        realestate: ["Investment grade assessment"],
      },
      clustering: {
        universal: ["Group discovery", "Pattern segmentation"],
        finance: ["Customer segmentation"],
        ecommerce: ["Customer behavior groups"],
        logistics: ["Route grouping"],
        tech: ["User persona groups"],
        realestate: ["Property market segments"],
      },
    };
    return useCases[modelType]?.[industry] || ["General predictive analysis"];
  };

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-4">Train New Model</h2>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Model Name</label>
          <Input placeholder="Enter model name" value={modelName} onChange={(e) => setModelName(e.target.value)} />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Industry</label>
          <Select value={industry} onValueChange={(v: any) => setIndustry(v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="universal">Universal / General</SelectItem>
              <SelectItem value="finance">Finance</SelectItem>
              <SelectItem value="ecommerce">E-commerce</SelectItem>
              <SelectItem value="logistics">Logistics</SelectItem>
              <SelectItem value="tech">Tech</SelectItem>
              <SelectItem value="realestate">Real Estate</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Model Type</label>
          <Select value={modelType} onValueChange={setModelType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="regression">Regression (Forecasting)</SelectItem>
              <SelectItem value="classification">Classification (Categories)</SelectItem>
              <SelectItem value="clustering">Clustering (Segmentation)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Training Data (CSV, XLSX, XLS, JSON)</label>
          <div className="flex items-center space-x-2">
            <Input type="file" accept=".csv,.xlsx,.xls,.json" onChange={handleFileChange} className="flex-1" />
            <Button variant="outline" onClick={() => { setFile(null); setSelectedDatasetColumns([]); }} disabled={!file}>Clear</Button>
          </div>
        </div>

        {selectedDatasetColumns.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-2">Target Variable</label>
            <Select value={targetVariable} onValueChange={setTargetVariable}>
              <SelectTrigger><SelectValue placeholder="Select target variable" /></SelectTrigger>
              <SelectContent>
                {selectedDatasetColumns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {isTraining && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm"><span>Training Progress</span><span>{trainingProgress}%</span></div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div className="bg-primary h-2.5 rounded-full transition-all duration-500" style={{ width: `${trainingProgress}%` }} />
            </div>
          </div>
        )}

        <Button onClick={handleTrainModel} disabled={isTraining || !file || !modelName || !targetVariable} className="w-full" size="lg">
          {isTraining ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Training AI Model...</>
            : <><ChartLine className="w-4 h-4 mr-2" />Train Model with AI</>}
        </Button>

        {trainingResult && (
          <div className="mt-6 p-4 bg-muted rounded-lg space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <h4 className="font-medium">Model Training Complete</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">{modelType === "regression" ? "R² Score:" : "Accuracy:"}</span>
                <div className="font-semibold text-green-600">{(trainingResult.accuracy * 100).toFixed(1)}%</div>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Model Type:</span>
                <div className="font-semibold capitalize">{trainingResult.model_type}</div>
              </div>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Training Summary:</span>
              <p className="text-sm mt-1">{trainingResult.training_summary}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Recommended Use Cases:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {trainingResult.recommended_use_cases.map((u, i) => <Badge key={i} variant="secondary" className="text-xs">{u}</Badge>)}
              </div>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Feature Importance:</span>
              <div className="mt-2 space-y-1">
                {Object.entries(trainingResult.feature_importance)
                  .sort(([, a], [, b]) => b - a).slice(0, 5)
                  .map(([feature, importance]) => (
                    <div key={feature} className="flex items-center justify-between text-sm">
                      <span>{feature}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.min(100, importance * 100)}%` }} />
                        </div>
                        <span className="text-xs text-muted-foreground">{(importance * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
